/**
 * Decision tab (FR-4.4, DC-1794 demo D4) — the judgement a human owns.
 *
 * "ARC defines what should be true. Compass shows what is known, missing, or
 * contradicted. The bank makes the decision." This tab is the third sentence,
 * and it is built so Compass cannot quietly make the decision instead:
 *
 *  - NOTHING is pre-selected. The judgement field opens empty, with no
 *    default and no recommendation derived from the findings;
 *  - the rationale is required — the client refuses an empty one, the schema
 *    refuses it, and the database refuses it. Three walls, because a decision
 *    without a reason is the artefact a regulator asks for and nobody has;
 *  - a second decision SUPERSEDES visibly. The prior judgement stays on the
 *    page, struck and dated, because an overwritten decision is a lost audit
 *    trail;
 *  - the decision is recorded against ONE run id, shown on the page, so it is
 *    never mistaken for a standing verdict about the use case;
 *  - DC-1806: when any of the run's findings is DISPUTED — a measurement a
 *    confirmed attestation contradicts (DC-1799) — the dispute is stated at
 *    the point of judgement, with the disputed findings one disclosure away.
 *    A flag, never a block: the bank still decides ("we cannot assume the
 *    human is right, especially when not independent in their role" — but a
 *    decision to proceed must never silently rest on contested evidence).
 *    Zero disputes renders no dispute chrome at all.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { usePermissions } from '../../hooks/useAuth';
import { LINE_LABEL, type RunDecision, type RunResults } from './assessmentTypes';
import { isDisputed } from './findingDispute';
import DecisionRecordPreview from './DecisionRecordPreview';
import ActorPicker from './ActorPicker';

/**
 * DC-1823 (design §3.5): the positions recorded beside ONE decision —
 * newest-last, full history. Rendered for superseded decisions too: a
 * dissent on a superseded decision was a dissent on THAT decision, and
 * supersession must not launder it away. Viewer-visible (it is part of the
 * record); only the recording form is role-gated.
 */
const DecisionPositions: React.FC<{ decision: RunDecision }> = ({ decision }) => {
  const positions = decision.positions ?? [];
  if (positions.length === 0) return null;
  return (
    <ul className="uc-decision-positions" data-testid={`decision-positions-${decision.id}`}>
      {positions.map((p) => (
        <li
          key={p.id}
          className={p.position === 'dissent' ? 'uc-position-dissent' : 'uc-position-concur'}
          data-testid={`decision-position-${p.id}`}
        >
          <span className="uc-position-stance">
            {p.position === 'dissent' ? 'Dissent' : 'Concurrence'}
          </span>{' '}
          — {p.actor.displayName}, {p.actor.roleTitle} · {LINE_LABEL[p.actor.line]} ·{' '}
          {new Date(p.createdAt).toLocaleString()}
          <p className="uc-position-basis">{p.basis}</p>
        </li>
      ))}
    </ul>
  );
};

interface ResultsEnvelope {
  data?: { results?: RunResults };
}

type LoadState =
  | { phase: 'loading' }
  | { phase: 'error' }
  | { phase: 'no_run' }
  | { phase: 'ready'; results: RunResults };

export const UseCaseDecision: React.FC<{
  useCaseId: string;
  useCaseRef?: string;
  onRecorded?: () => void;
}> = ({ useCaseId, useCaseRef, onRecorded }) => {
  const { get, post } = useApiClient();
  // The same gate the server enforces (requireAnalystOrAdmin) — role, not
  // journey/dataset permission, so the surface cannot drift from the route.
  const { isAdmin, isAnalyst } = usePermissions();
  const canEdit = isAdmin || isAnalyst;
  const [state, setState] = useState<LoadState>({ phase: 'loading' });
  const [judgement, setJudgement] = useState('');
  const [rationale, setRationale] = useState('');
  const [owner, setOwner] = useState('');
  // DC-1823: optional registry binding for the owner — INSERT only.
  const [ownerActorId, setOwnerActorId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);

  // DC-1823: the position form. No default stance — concur is never assumed,
  // the same no-pre-selection rule as the judgement.
  const [posActorId, setPosActorId] = useState<string | null>(null);
  const [posStance, setPosStance] = useState<'concur' | 'dissent' | null>(null);
  const [posBasis, setPosBasis] = useState('');
  const [posSaving, setPosSaving] = useState(false);
  const [posRefusal, setPosRefusal] = useState<string | null>(null);

  const load = useCallback(async () => {
    setState({ phase: 'loading' });
    try {
      const res = await get<ResultsEnvelope>(`/api/use-cases/${useCaseId}/assessment-runs/latest`);
      const results = res.data?.results;
      setState(results ? { phase: 'ready', results } : { phase: 'no_run' });
    } catch (error) {
      const status = (error as { status?: number }).status;
      setState(status === 404 ? { phase: 'no_run' } : { phase: 'error' });
    }
  }, [get, useCaseId]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = useCallback(
    async (runId: string) => {
      setSaving(true);
      setRefusal(null);
      try {
        await post(`/api/use-cases/${useCaseId}/assessment-runs/${runId}/decisions`, {
          judgement: judgement.trim(),
          rationale: rationale.trim(),
          owner: owner.trim(),
          ...(ownerActorId ? { ownerActorId } : {}),
        });
        setJudgement('');
        setRationale('');
        setOwner('');
        setOwnerActorId(null);
        await load();
        onRecorded?.();
      } catch (error) {
        setRefusal(
          (error as { body?: { error?: { message?: string } } }).body?.error?.message ??
            'The decision was not recorded.'
        );
      } finally {
        setSaving(false);
      }
    },
    [post, useCaseId, judgement, rationale, owner, ownerActorId, load, onRecorded]
  );

  const submitPosition = useCallback(
    async (runId: string, decisionId: string) => {
      if (!posActorId || !posStance) return;
      setPosSaving(true);
      setPosRefusal(null);
      try {
        await post(
          `/api/use-cases/${useCaseId}/assessment-runs/${runId}/decisions/${decisionId}/positions`,
          { actorId: posActorId, position: posStance, basis: posBasis.trim() }
        );
        setPosActorId(null);
        setPosStance(null);
        setPosBasis('');
        await load();
      } catch (error) {
        setPosRefusal(
          (error as { body?: { error?: { message?: string } } }).body?.error?.message ??
            'The position was not recorded.'
        );
      } finally {
        setPosSaving(false);
      }
    },
    [post, useCaseId, posActorId, posStance, posBasis, load]
  );

  if (state.phase === 'loading') return <div className="uc-decision" aria-busy="true" />;

  if (state.phase === 'error') {
    return (
      <div className="uc-decision">
        <div className="banking-grid-error" role="alert">
          <p>The decision record could not be read.</p>
          <button type="button" className="banking-btn banking-btn-secondary" onClick={() => void load()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (state.phase === 'no_run') {
    return (
      <section className="uc-decision" aria-label="Decision">
        <p className="uc-decision-lede">
          A decision is recorded against a specific assessment run. No run exists for this use case
          yet, so there is nothing to decide on — run the assessment first.
        </p>
      </section>
    );
  }

  const { run, decisions, disputedCount, selfAttestedOnlyCount, findings } = state.results;
  const current = decisions.find((d) => d.supersededBy === null) ?? null;
  const superseded = decisions.filter((d) => d.supersededBy !== null);
  const disputedFindings = findings.filter(isDisputed);
  // DC-1805: the server computes the count; the client only identifies
  // WHICH rows for the drill-down — the same split as disputes.
  const selfAttestedFindings = findings.filter((f) => f.accountability?.selfAttestedOnly);
  const canSubmit =
    judgement.trim().length > 0 && rationale.trim().length > 0 && owner.trim().length > 0;

  return (
    <section className="uc-decision" aria-label="Decision">
      <p className="uc-decision-lede">
        Compass does not decide. It records what a named person decided, why, and against which run
        — this one, of {new Date(run.createdAt).toLocaleString()}.
      </p>

      {/* DC-1806: contested evidence is stated where the judgement happens.
          Rendered only when a dispute exists — the flag never cries wolf. */}
      {disputedCount > 0 && (
        <div className="uc-decision-disputes" role="note" data-testid="decision-disputes">
          <p className="uc-decision-disputes-lede">
            {disputedCount === 1
              ? 'One finding on this run is disputed'
              : `${disputedCount} findings on this run are disputed`}{' '}
            — a measured outcome a confirmed attestation contradicts. A decision recorded here is a
            decision over contested evidence, and the record will show the dispute beside it.
          </p>
          <details className="uc-decision-disputes-detail">
            <summary data-testid="decision-disputes-toggle">See the disputed findings</summary>
            <ul>
              {disputedFindings.map((finding) => {
                const speakers = finding.evidence
                  .filter((item) => item.interview?.stance === 'contradicts')
                  .map((item) => item.interview?.speaker)
                  .filter(Boolean);
                return (
                  <li key={finding.testCode} data-testid={`decision-dispute-${finding.testCode}`}>
                    <span className="uc-decision-dispute-code">{finding.testCode}</span>
                    {finding.testName && (
                      <span className="uc-decision-dispute-name"> {finding.testName}</span>
                    )}
                    <span className="uc-decision-dispute-outcome">
                      {' '}
                      — the {finding.outcome.replace(/_/g, ' ')} outcome stands
                      {speakers.length > 0 ? `; disputed by ${speakers.join(', ')}` : ''}. Full
                      detail on the Results tab.
                    </span>
                  </li>
                );
              })}
            </ul>
          </details>
        </div>
      )}

      {/* DC-1805: the independence flag at the point of judgement — the
          DC-1806 pattern verbatim. A flag, never a block: the bank still
          decides, and the record shows what the decision rested on. */}
      {selfAttestedOnlyCount > 0 && (
        <div
          className="uc-decision-disputes uc-decision-selfattested"
          role="note"
          data-testid="decision-selfattested"
        >
          <p className="uc-decision-disputes-lede">
            {selfAttestedOnlyCount === 1
              ? 'One finding on this run is answered only by first-line attestations'
              : `${selfAttestedOnlyCount} findings on this run are answered only by first-line attestations`}{' '}
            — the party that owns the risk certified its own controls, with no independent
            challenge on the record. The outcomes stand undiscounted; a decision recorded here
            visibly rests on them.
          </p>
          <details className="uc-decision-disputes-detail">
            <summary data-testid="decision-selfattested-toggle">
              See the first-line-only findings
            </summary>
            <ul>
              {selfAttestedFindings.map((finding) => (
                <li
                  key={finding.testCode}
                  data-testid={`decision-selfattested-${finding.testCode}`}
                >
                  <span className="uc-decision-dispute-code">{finding.testCode}</span>
                  {finding.testName && (
                    <span className="uc-decision-dispute-name"> {finding.testName}</span>
                  )}
                  <span className="uc-decision-dispute-outcome">
                    {' '}
                    — supplied by{' '}
                    {(finding.accountability?.suppliedBy ?? [])
                      .map((actor) => `${actor.displayName} (${actor.roleTitle})`)
                      .join('; ')}
                    . Full detail on the Results tab.
                  </span>
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {current ? (
        <div className="uc-decision-current" data-testid="current-decision">
          <p className="uc-decision-judgement">{current.judgement}</p>
          <p className="uc-decision-rationale">{current.rationale}</p>
          <p className="uc-decision-meta">
            Owner {current.owner}
            {/* DC-1823: the registry binding gives the owner a title and a
                line; the free-text owner still prints (export contract). */}
            {current.ownerActor && (
              <span data-testid="decision-owner-actor">
                {' '}
                ({current.ownerActor.roleTitle} · {LINE_LABEL[current.ownerActor.line]})
              </span>
            )}{' '}
            · recorded by {current.decidedByName} · {new Date(current.decidedAt).toLocaleString()}
          </p>
          <DecisionPositions decision={current} />
          {/* DC-1822: the circulate-able one-pager lives beside the decision it
              records. Viewer-permitted, exactly like the server route. */}
          {run.status === 'complete' && (
            <div className="uc-decision-record-actions">
              <DecisionRecordPreview
                useCaseId={useCaseId}
                runId={run.id}
                decisionId={current.id}
                useCaseRef={useCaseRef ?? useCaseId.slice(0, 8)}
              />
            </div>
          )}
        </div>
      ) : (
        <div data-testid="no-decision">
          <p className="uc-decision-none">No decision has been recorded against this run.</p>
          {/* The 409's honest sentence, stated before anyone hits it. */}
          <div className="uc-decision-record-actions">
            <button type="button" className="banking-btn banking-btn-secondary" disabled>
              Preview decision record
            </button>
            <p className="uc-decision-hint">Record a decision to issue this one-pager.</p>
          </div>
        </div>
      )}

      {superseded.length > 0 && (
        <div className="uc-decision-superseded" data-testid="superseded-decisions">
          <h4>Superseded</h4>
          <ul>
            {superseded.map((decision) => (
              <li key={decision.id}>
                <s>{decision.judgement}</s>
                <span className="uc-decision-meta">
                  {' '}
                  — {decision.owner} · {new Date(decision.decidedAt).toLocaleString()}
                </span>
                <p className="uc-decision-rationale">{decision.rationale}</p>
                {/* DC-1823: positions were positions on THAT decision —
                    supersession never launders a dissent away. */}
                <DecisionPositions decision={decision} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* DC-1823 (design §3.5/§5.2): record a position beside the CURRENT
          decision. A position needs a decision to be about; the actor is
          required (a position is someone's); the stance has no default; the
          basis is required — the same three walls as the decision itself. */}
      {canEdit && current && (
        <form
          className="uc-decision-form uc-position-form"
          data-testid="position-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (posActorId && posStance && posBasis.trim().length > 0) {
              void submitPosition(run.id, current.id);
            }
          }}
        >
          <h4>Record a position on the current decision</h4>
          <p className="uc-decision-hint">
            A concurrence or dissent recorded beside the decision — it never gates or invalidates
            the judgement, and it stays on the record even if the decision is later superseded.
          </p>
          <ActorPicker
            idPrefix="uc-position"
            label="Whose position"
            value={posActorId}
            onChange={setPosActorId}
            disabled={posSaving}
          />
          <fieldset className="uc-position-stance-choice">
            <legend>Position</legend>
            {/* No default: concur is never assumed. */}
            <label htmlFor="uc-position-concur">
              <input
                id="uc-position-concur"
                type="radio"
                name="uc-position-stance"
                checked={posStance === 'concur'}
                onChange={() => setPosStance('concur')}
              />{' '}
              Concur
            </label>
            <label htmlFor="uc-position-dissent">
              <input
                id="uc-position-dissent"
                type="radio"
                name="uc-position-stance"
                checked={posStance === 'dissent'}
                onChange={() => setPosStance('dissent')}
              />{' '}
              Dissent
            </label>
          </fieldset>
          <label htmlFor="uc-position-basis">Basis (required)</label>
          <textarea
            id="uc-position-basis"
            value={posBasis}
            maxLength={2000}
            rows={3}
            onChange={(event) => setPosBasis(event.target.value)}
          />
          <button
            type="submit"
            className="banking-btn banking-btn-primary"
            data-testid="record-position"
            disabled={!posActorId || !posStance || posBasis.trim().length === 0 || posSaving}
          >
            {posSaving ? 'Recording…' : 'Record position'}
          </button>
          {(!posActorId || !posStance || posBasis.trim().length === 0) && (
            <p className="uc-decision-hint">
              A named actor, a stated position, and its basis are all required — a position
              without a basis is not a record anyone can weigh.
            </p>
          )}
          {posRefusal && (
            <p className="uc-decision-refusal" role="alert">
              {posRefusal}
            </p>
          )}
        </form>
      )}

      {canEdit && (
        <form
          className="uc-decision-form"
          onSubmit={(event) => {
            event.preventDefault();
            if (canSubmit) void submit(run.id);
          }}
        >
          <h4>{current ? 'Record a new decision (supersedes the current one)' : 'Record the decision'}</h4>
          <label htmlFor="uc-decision-judgement">Judgement</label>
          {/* No default, no suggestion, no derived recommendation. */}
          <input
            id="uc-decision-judgement"
            type="text"
            value={judgement}
            maxLength={200}
            placeholder="e.g. Authorized, Conditionally authorized, Not authorized"
            onChange={(event) => setJudgement(event.target.value)}
          />
          <label htmlFor="uc-decision-rationale">Rationale (required)</label>
          <textarea
            id="uc-decision-rationale"
            value={rationale}
            maxLength={4000}
            rows={4}
            onChange={(event) => setRationale(event.target.value)}
          />
          <label htmlFor="uc-decision-owner">Accountable owner</label>
          <input
            id="uc-decision-owner"
            type="text"
            value={owner}
            maxLength={300}
            onChange={(event) => setOwner(event.target.value)}
          />
          {/* DC-1823: optionally bind the owner to a registry actor, which
              gives the decision an owner with a line and a title. Set at
              record time only — never editable after. */}
          <ActorPicker
            idPrefix="uc-decision-owner"
            label="Owner in the registry (optional)"
            value={ownerActorId}
            onChange={setOwnerActorId}
            disabled={saving}
          />
          <button
            type="submit"
            className="banking-btn banking-btn-primary"
            data-testid="record-decision"
            disabled={!canSubmit || saving}
          >
            {saving ? 'Recording…' : 'Record decision'}
          </button>
          {!canSubmit && (
            <p className="uc-decision-hint">
              A judgement, a rationale, and an accountable owner are all required — a decision
              without a reason is not a record anyone can defend.
            </p>
          )}
          {refusal && (
            <p className="uc-decision-refusal" role="alert">
              {refusal}
            </p>
          )}
        </form>
      )}
    </section>
  );
};

export default UseCaseDecision;
